/*
  <cart-add-toast> — transient post-add confirmation.

  Fires on PUB_SUB_EVENTS.cartUpdate when source is 'product-form' or
  'sticky-add-to-cart' (not on cart-items quantity changes). Re-GETs /cart.js
  for authoritative post-add state, classifies the just-added line as pack
  vs non-pack via the _glend_pack line-item property, and shows one of three
  copy states. Auto-hides after 3s. Multiple rapid adds: latest replaces.

  Threshold: 6000 cents (€60). Convention: >= 6000 is "free shipping".
  No exclamations, even on the unlock state.
*/

if (!customElements.get('cart-add-toast')) {
  class CartAddToast extends HTMLElement {
    constructor() {
      super();
      this.textEl = null;
      this.hideTimer = null;
      this.HIDE_AFTER_MS = 3000;
      this.THRESHOLD_CENTS = 6000;
      this.strings = {
        packAdded: '',
        addedProgressTemplate: '',
        addedFree: '',
      };
    }

    connectedCallback() {
      this.textEl = this.querySelector('.cart-add-toast__text');
      this.strings.packAdded = this.dataset.stringPackAdded || 'Pack añadido · envío gratis';
      this.strings.addedProgressTemplate = this.dataset.stringAddedProgress
        || 'Producto añadido · te quedan __AMOUNT__ € para envío gratis';
      this.strings.addedFree = this.dataset.stringAddedFree || 'Producto añadido · envío gratis';
      this.trySubscribe();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      if (this.hideTimer) clearTimeout(this.hideTimer);
    }

    trySubscribe() {
      var self = this;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          self.onCartUpdate(event);
        });
      } else {
        setTimeout(function () { self.trySubscribe(); }, 500);
      }
    }

    onCartUpdate(event) {
      if (!event) return;
      // Only fire on add-to-cart paths. Skip cart-items quantity changes and
      // any other source that isn't a fresh add.
      var src = event.source;
      if (src !== 'product-form' && src !== 'sticky-add-to-cart') return;

      var addedVariantId = event.productVariantId
        ? parseInt(event.productVariantId, 10)
        : null;

      // Re-GET /cart.js for authoritative post-add state.
      var self = this;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) { self.render(cart, addedVariantId); })
        .catch(function (err) { console.error('cart-add-toast: cart.js fetch failed', err); });
    }

    render(cart, addedVariantId) {
      if (!cart || !cart.items) return;

      // Classify the just-added line and compute the non-pack subtotal.
      var addedIsPack = false;
      var nonPackSubtotalCents = 0;
      var addedLine = null;
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        var isPack = !!(it.properties && it.properties._glend_pack);
        if (!isPack) {
          nonPackSubtotalCents += it.final_line_price || 0;
        }
        if (addedVariantId && it.variant_id === addedVariantId) {
          addedLine = it;
          addedIsPack = isPack;
        }
      }

      var text;
      var isFreeState = false;
      if (addedIsPack) {
        // Pack added — unconditional free shipping promise.
        text = this.strings.packAdded;
        isFreeState = true;
      } else if (nonPackSubtotalCents >= this.THRESHOLD_CENTS) {
        // Non-pack at/above threshold (post-add).
        text = this.strings.addedFree;
        isFreeState = true;
      } else {
        // Non-pack below threshold — interpolate remaining amount.
        var remainingCents = this.THRESHOLD_CENTS - nonPackSubtotalCents;
        var amountStr = this.formatMoney(remainingCents, cart.currency);
        text = this.strings.addedProgressTemplate.replace('__AMOUNT__', amountStr);
      }

      this.textEl.textContent = text;
      this.classList.toggle('is-free', isFreeState);
      this.show();
    }

    formatMoney(cents, currency) {
      // Bare number only ("53,20") — the € symbol lives in the locale string
      // (cart.toast_added_progress carries "{{ amount }} €"), so appending it
      // here double-prints the symbol.
      var euros = cents / 100;
      var formatted = euros.toFixed(2).replace('.', ',');
      // Strip trailing ",00" for whole-euro amounts → "20" reads cleaner
      // than "20,00" in compact toast copy.
      if (formatted.endsWith(',00')) {
        formatted = formatted.slice(0, -3);
      }
      return formatted;
    }

    show() {
      this.removeAttribute('hidden');
      this.setAttribute('visible', '');
      if (this.hideTimer) clearTimeout(this.hideTimer);
      var self = this;
      this.hideTimer = setTimeout(function () { self.hide(); }, this.HIDE_AFTER_MS);
    }

    hide() {
      this.removeAttribute('visible');
      // Keep the element in the DOM (hidden) so transitions can play; the
      // attribute toggle drives display:none via CSS.
      this.hideTimer = null;
    }
  }

  customElements.define('cart-add-toast', CartAddToast);
}
